import { Input } from "antd"
import {
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"

export interface SearchBarRef {
  clear: () => void
}

const SearchBar = forwardRef<
  SearchBarRef,
  {
    onSearch: (search: string) => void
    placeholder?: string
    loading?: boolean
    defaultSearchTerm?: string
  }
>(
  (
    { onSearch, placeholder = "Search", loading, defaultSearchTerm = "" },
    ref
  ) => {
    const [searchValue, setSearchValue] = useState(defaultSearchTerm)
    const rafRef = useRef<number>()

    useImperativeHandle(ref, () => ({
      clear: () => {
        // Do not call onSearch here because it will race condition other settings changes
        setSearchValue("")
      },
    }))

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
      const searchStr = e.target.value
      setSearchValue(searchStr)

      // Clear previous RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }

      // Schedule search with RAF
      rafRef.current = requestAnimationFrame(() => {
        onSearch(searchStr)
      })
    }

    // Cleanup RAF on unmount
    useEffect(() => {
      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
        }
      }
    }, [])

    return (
      <Input.Search
        style={{ width: 400 }}
        onChange={handleSearch}
        value={searchValue}
        placeholder={placeholder}
        loading={loading}
        allowClear
      />
    )
  }
)

SearchBar.displayName = "SearchBar"

export default SearchBar
